import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import DOMPurify from "dompurify";
import { FiPaperclip } from "react-icons/fi";
import { toast } from "react-toastify";
import { BASE_URL } from "../utils";

const subStatusOptions = [
  "NA",
  "Research",
  "Follow Up",
  "Not Selling",
  "Evernote",
  "Selling",
  "fresh lead",
];
const followUpRemarkOptions = [
  "Selling",
  "Not Selling",
  "Undecided",
  "Left Voicemail",
  "NA",
  "Call Back Requested",
  "Meeting Scheduled",
  "Important in follow up",
];

const stripHtml = (html) =>
  String(html || "")
    .replace(/<[^>]*>/g, "")
    .replace(/\u00A0/g, " ")
    .trim();

const escapeHtml = (str) =>
  String(str || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const renderNoteContent = (text) => {
  if (!text) return { __html: "—" };
  const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(text);
  if (looksLikeHtml) return { __html: text };
  return { __html: escapeHtml(text).replace(/\n/g, "<br/>") };
};

const buildPdfHref = (pdfUrl) => {
  if (!pdfUrl) return null;
  if (pdfUrl.startsWith("http://") || pdfUrl.startsWith("https://")) return pdfUrl;
  const path = pdfUrl.startsWith("/") ? pdfUrl : `/${pdfUrl}`;
  return `${BASE_URL}${path}`;
};

function NoteItem({ note, onEdit, onDelete }) {
  const pdfHref = buildPdfHref(note.pdf_url || note.pdfUrl || "");

  return (
    <div
      className={`text-sm mb-3 pl-2 border-l-4 ${
        String(note.status || "").toLowerCase() === "approved" ? "border-green-500" : "border-gray-300"
      }`}
    >
      <div
        className="text-gray-800 whitespace-pre-line mb-2"
        dangerouslySetInnerHTML={renderNoteContent(note.note || note.text || "")}
      />
      {pdfHref && (
        <div className="mb-2">
          <a
            href={pdfHref}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-blue-600 underline inline-flex items-center gap-1"
          >
            <FiPaperclip size={12} /> View Attached PDF
          </a>
        </div>
      )}
      <div className="text-[11px] text-gray-500 flex items-center justify-between">
        <span>{note.created_at ? new Date(note.created_at).toLocaleString() : note.timestamp || ""}</span>
        <div className="flex gap-3 items-center">
          <button onClick={onEdit} className="text-indigo-600 text-xs">
            ✏️ Edit
          </button>
          <button onClick={onDelete} className="text-red-500 text-xs">
            🗑️ Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function StatusNotesPanel({ entityId, apiBase, title = "Status & Notes" }) {
  const editorRef = useRef(null);
  const [notes, setNotes] = useState([]);
  const [statusNotes, setStatusNotes] = useState({});
  const [activeStatus, setActiveStatus] = useState("Approved");
  const [subStatus, setSubStatus] = useState("NA");
  const [label, setLabel] = useState("");
  const [followUpRemark, setFollowUpRemark] = useState("");
  const [followUpDate, setFollowUpDate] = useState("");
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentNoteId, setCurrentNoteId] = useState(null);
  const [uploadedPdfUrl, setUploadedPdfUrl] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);

  const base = apiBase.startsWith("http") ? apiBase : `${BASE_URL}${apiBase}`;
  const encodedId = encodeURIComponent(entityId || "");

  const fetchNotes = async () => {
    if (!entityId) return;
    setLoadingNotes(true);
    try {
      const res = await axios.get(`${base}/${encodedId}/notes`);
      const list = Array.isArray(res?.data?.notes) ? res.data.notes : [];
      setNotes(list);

      const grouped = list.reduce((acc, n) => {
        const key = n.status || "Approved";
        acc[key] = acc[key] || [];
        acc[key].push(n);
        return acc;
      }, {});
      setStatusNotes(grouped);

      const current = res?.data?.current || {};
      setActiveStatus(current.status || "Approved");
      setSubStatus(current.sub_status || "NA");
      setLabel(current.label || "");
      setFollowUpRemark(current.remark || "");
      setFollowUpDate(current.followUp_date || "");
    } catch (err) {
      console.error("status notes fetch error", err);
      toast.error("Failed to load notes.");
    } finally {
      setLoadingNotes(false);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [entityId]);

  const handleEditorPaste = (e) => {
    if (!e.clipboardData) return;
    e.preventDefault();
    const html = e.clipboardData.getData("text/html");
    const text = e.clipboardData.getData("text/plain");
    if (html) insertHtmlAtCaret(html);
    else if (text) insertHtmlAtCaret(text.replace(/\r\n|\r|\n/g, "<br>"));
  };

  function insertHtmlAtCaret(html) {
    try {
      document.execCommand("insertHTML", false, html);
      return;
    } catch {}
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) {
      if (editorRef.current) editorRef.current.insertAdjacentHTML("beforeend", html);
      return;
    }
    const range = sel.getRangeAt(0);
    range.deleteContents();
    const el = document.createElement("div");
    el.innerHTML = html;
    const frag = document.createDocumentFragment();
    let node;
    while ((node = el.firstChild)) frag.appendChild(node);
    range.insertNode(frag);
    if (frag.lastChild) {
      range.setStartAfter(frag.lastChild);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }

  const handlePdfUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      return toast.error("Please upload a PDF file.");
    }

    const formData = new FormData();
    formData.append("pdf", file);

    try {
      setUploadingPdf(true);
      const uploadRes = await axios.post(`${BASE_URL}/api/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const uploadedUrl =
        uploadRes?.data?.url ||
        uploadRes?.data?.fileURL ||
        uploadRes?.data?.fileUrl ||
        uploadRes?.data?.url;
      if (!uploadedUrl) {
        throw new Error("Upload returned no URL.");
      }
      setUploadedPdfUrl(uploadedUrl);
      toast.success("📄 PDF uploaded successfully and ready to attach.");
    } catch (err) {
      console.error("PDF upload error:", err);
      toast.error("Failed to upload PDF.");
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleFollowUpPick = (date) => {
    if (!date) return;
    const formattedDisplay = date.toLocaleDateString("en-US");
    const isoDate = date.toISOString().split("T")[0];
    setFollowUpDate(isoDate);
    setShowCalendar(false);
    if (editorRef.current) {
      const existing = editorRef.current.innerHTML || "";
      const stamp = `<div><strong>Follow-up:</strong> ${formattedDisplay}</div>`;
      editorRef.current.innerHTML = `${stamp}${existing}`;
    }
  };

  const handleSaveStatus = async () => {
    const editorHtml = editorRef.current ? editorRef.current.innerHTML : "";
    const sanitized = editorHtml ? DOMPurify.sanitize(editorHtml, { ADD_ATTR: ["style", "class", "data-*"] }) : "";
    const plain = stripHtml(sanitized);

    if (activeStatus !== "Rejected" && !plain.trim() && !uploadedPdfUrl) {
      return alert("⚠️ Please enter a note or attach a PDF before saving status.");
    }
    if (activeStatus === "Approved") {
      if (!subStatus || subStatus === "") {
        return alert("⚠️ Please choose a valid Sub-Status before saving (cannot be NA).");
      }
      if (!label || label === "") {
        return alert("⚠️ Please select a Label before saving.");
      }
    }
    if (subStatus === "Follow Up") {
      if (!followUpRemark || followUpRemark.trim() === "") {
        return alert("Please select a Remark for Follow Up.");
      }
      const match = plain.match(/\b\d{1,2}\/\d{1,2}\/\d{4}\b/);
      if (!match && !followUpDate) {
        return alert("⚠️ Please include a follow-up date in MM/DD/YYYY format or pick a date.");
      }
    }

    setSaveLoading(true);
    try {
      if (currentNoteId) {
        await axios.put(`${base}/${encodedId}/notes/${currentNoteId}`, {
          note: sanitized,
          plain_text: plain,
          pdf_url: uploadedPdfUrl || null,
        });
        toast.success("Note updated.");
      } else {
        await axios.post(`${base}/${encodedId}/notes`, {
          status: activeStatus,
          note: sanitized,
          plain_text: plain,
          sub_status: activeStatus === "Approved" ? subStatus : null,
          label: activeStatus === "Approved" ? label : null,
          remark: activeStatus === "Approved" && subStatus === "Follow Up" ? followUpRemark : null,
          followUp_date: followUpDate || null,
          pdf_url: uploadedPdfUrl || null,
        });
        toast.success("✅ Status saved successfully.");
      }

      setUploadedPdfUrl(null);
      setCurrentNoteId(null);
      if (editorRef.current) editorRef.current.innerHTML = "";
      await fetchNotes();
    } catch (err) {
      console.error("Error saving status:", err);
      toast.error("❌ Failed to save status.");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteNote = async (note) => {
    const id = note.id || note.noteId;
    if (!id) return alert("Cannot delete: no id.");
    if (!window.confirm("Delete this note?")) return;
    try {
      await axios.delete(`${base}/${encodedId}/notes/${id}`);
      toast.success("Note deleted.");
      await fetchNotes();
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Failed to delete note.");
    }
  };

  const loadNoteIntoEditorForEdit = (note) => {
    if (!note) return;
    setCurrentNoteId(note.noteId || note.id || null);
    if (editorRef.current) editorRef.current.innerHTML = note.note || note.text || "";
    setUploadedPdfUrl(note.pdf_url || note.pdfUrl || null);
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.info("Loaded note into editor for editing.");
  };

  return (
    <div className="border rounded-lg p-4 bg-gray-50">
      <style>{`
        .raw-html-preview { box-sizing: border-box; max-width: 100%; overflow-x: auto; overflow-y: auto; word-wrap: break-word; }
        .raw-html-preview * { box-sizing: border-box; max-width: 100% !important; }
        .raw-html-preview img, .raw-html-preview video, .raw-html-preview iframe { max-width: 100% !important; height: auto !important; display: block; }
        .raw-html-preview table { width: 100% !important; table-layout: fixed !important; border-collapse: collapse; }
        .raw-html-preview td, .raw-html-preview th { word-break: break-word !important; white-space: normal !important; }
        .raw-html-preview pre { white-space: pre-wrap !important; word-break: break-word !important; max-width: 100% !important; overflow-x: auto; }
      `}</style>

      <div className="flex items-start justify-between mb-3 gap-3">
        <div>
          <h3 className="font-semibold text-lg mb-0">📋 {title}</h3>
          <div className="text-xs text-gray-500">Write notes (HTML supported).</div>
        </div>
        {loadingNotes && <div className="text-xs text-gray-500">Loading…</div>}
      </div>

      <div className="flex gap-2 mb-3">
        {["Approved", "Rejected"].map((status) => (
          <button
            key={status}
            onClick={() => {
              setActiveStatus(status);
              setFollowUpDate("");
              setSubStatus("");
              setFollowUpRemark("");
            }}
            className={`px-2 py-1 rounded text-sm font-medium ${
              activeStatus === status ? "bg-indigo-600 text-white" : "bg-white border"
            }`}
          >
            {status === "Approved" ? "Lead" : "Rejected"}
          </button>
        ))}
      </div>

      {activeStatus === "Approved" && (
        <>
          <div className="flex gap-2 mb-3">
            <div className="flex-1">
              <label className="text-sm font-medium block mb-1">Sub-Status</label>
              <select
                value={subStatus}
                onChange={(e) => {
                  const selected = e.target.value;
                  setSubStatus(selected);
                  setShowCalendar(selected === "Follow Up");
                  if (selected !== "Follow Up") {
                    setFollowUpDate("");
                    setFollowUpRemark("");
                  }
                }}
                className="w-full border px-2 py-1 rounded text-sm"
              >
                {subStatusOptions.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>

            {subStatus === "Follow Up" && (
              <div className="flex-1">
                <label className="text-sm font-medium block mb-1">Remark</label>
                <select
                  value={followUpRemark}
                  onChange={(e) => setFollowUpRemark(e.target.value)}
                  className="w-full border px-2 py-1 rounded text-sm"
                >
                  <option value="">Select remark...</option>
                  {followUpRemarkOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex-1">
              <label className="text-sm font-medium block mb-1">Label</label>
              <select
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                className="w-full border px-2 py-1 rounded text-sm"
              >
                <option value="">Select label…</option>
                <option value="Hot">Hot</option>
                <option value="Warm">Warm</option>
                <option value="Cold">Cold</option>
              </select>
            </div>
          </div>

          {subStatus === "Follow Up" && showCalendar && (
            <div className="mb-3">
              <label className="text-sm block mb-1">📆 Select Follow-up Date</label>
              <DatePicker
                selected={followUpDate ? new Date(followUpDate) : null}
                onChange={(date) => handleFollowUpPick(date)}
                dateFormat="MM/dd/yyyy"
                className="border px-2 py-1 rounded text-sm w-full"
                placeholderText="Select a date"
                inline
              />
            </div>
          )}
        </>
      )}

      <div className="text-sm text-gray-700 mb-2">
        Current Status: <span className="font-semibold">{activeStatus}</span>
      </div>

      <div className="mb-3 bg-white p-2 rounded">
        <div
          ref={editorRef}
          contentEditable
          onPaste={handleEditorPaste}
          suppressContentEditableWarning
          style={{
            minHeight: 200,
            border: "1px solid #e5e7eb",
            padding: 8,
            borderRadius: 6,
            background: "#fff",
            overflow: "auto",
            maxWidth: "100%",
          }}
          className={`text-sm ${activeStatus === "Rejected" ? "pointer-events-none opacity-60" : ""}`}
          aria-label="Rich editor"
        />
      </div>

      <div className="mb-3">
        <label className="text-sm font-medium block mb-1">📤 Attach PDF (optional)</label>
        <input type="file" accept="application/pdf" onChange={handlePdfUpload} disabled={uploadingPdf} />
        {uploadedPdfUrl && (
          <div className="mt-1 text-xs text-green-600">
            ✅ Attached:{" "}
            <a
              href={uploadedPdfUrl.startsWith("http") ? uploadedPdfUrl : `${BASE_URL}${uploadedPdfUrl}`}
              target="_blank"
              rel="noreferrer"
              className="underline text-blue-600"
            >
              View PDF
            </a>
          </div>
        )}
      </div>

      <div className="flex gap-2 mb-3 items-center">
        <button
          onClick={handleSaveStatus}
          disabled={
            saveLoading ||
            (activeStatus === "Approved" && (!label || label === "")) ||
            (activeStatus === "Approved" && subStatus === "Follow Up" && (!followUpRemark || followUpRemark === ""))
          }
          className={`bg-gradient-to-r from-green-400 to-blue-500 text-white text-sm px-3 py-1 rounded font-medium self-start hover:from-green-500 hover:to-blue-600 ${
            saveLoading ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          💾 {saveLoading ? "Saving..." : "Save Status"}
        </button>

        <button
          onClick={() => {
            if (editorRef.current) editorRef.current.innerHTML = "";
            setCurrentNoteId(null);
            setUploadedPdfUrl(null);
          }}
          className="text-sm px-3 py-1 border rounded"
        >
          Clear
        </button>

        <div className="text-xs text-gray-500 self-center">
          Notes save raw HTML (if pasted) or editor HTML. You can optionally attach a PDF.
        </div>
      </div>

      <div className="overflow-auto max-h-[40vh]">
        {(statusNotes[activeStatus] || []).map((note) => (
          <NoteItem
            key={note.noteId || note.id || note.timestamp}
            note={note}
            onEdit={() => loadNoteIntoEditorForEdit(note)}
            onDelete={() => handleDeleteNote(note)}
          />
        ))}
      </div>
    </div>
  );
}
